import { Alert, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import { SyntheticEvent, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DialogButton from '../../../Common/Buttons/DialogButton'
import Dialog from '../../../Common/Dialog/Dialog'
import DialogActions from '../../../Common/Dialog/DialogActions'
import DialogContent from '../../../Common/Dialog/DialogContent'
import DialogTitle from '../../../Common/Dialog/DialogTitle'
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../../Common/_hooks/useAppSelector'
import { BatchDeleteStoreProducts, type BatchStoreProductSelection } from '../../../UserData/_actions/Store/StoreProductActions'
import { ClearStoreSelectedProduct } from '../../../UIData/_actions/Account/AccountStoreActions'

interface Props {
  opened: boolean
  storeName: string
  storeType: string
  selection: BatchStoreProductSelection
  selectionCount: number
  onClose: () => void
}

export default function BatchDeleteStoreProductsDialog(props: Props) {
  const [t] = useTranslation('account-v2')
  const dispatch = useAppDispatch()
  const loader = useAppSelector(state => state.get('UIData').get('loaders').get('edit_store_products'))
  const [confirm, setConfirm] = useState(false)

  useEffect(() => {
    if (!props.opened) {
      setConfirm(false)
    }
  }, [props.opened])

  const onChangeConfirm = useCallback((e: SyntheticEvent, checked: boolean) => {
    setConfirm(checked)
  }, [])

  const onConfirm = useCallback(() => {
    dispatch(BatchDeleteStoreProducts(props.storeType, props.selection).set({
      onSuccess: () => {
        dispatch(ClearStoreSelectedProduct())
        props.onClose()
      }
    }))
  }, [dispatch, props])

  return <Dialog
    open={props.opened}
    maxWidth="xs"
    fullWidth
    onClose={props.onClose}
    showCloseButton
  >
    <DialogTitle>{t('Delete {{count}} products from {{store}}', { count: props.selectionCount, store: props.storeName })}</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant="body1">{t('Are you sure that you want to permanently delete the selected products from your {{store}} store?', { store: props.storeName })}</Typography>
        <FormControlLabel
          label={t('I confirm that I want to delete these products from both Art of Where and {{store}}', { store: props.storeName })}
          control={<Checkbox />}
          checked={confirm}
          disabled={!!loader}
          onChange={onChangeConfirm}
        />
        <Alert severity="info">{t('If you only want to hide them from Art of Where, you can use the "Ignore" feature and use the filters at the top to only show synced products.')}</Alert>
      </Stack>
    </DialogContent>
    <DialogActions>
      <DialogButton
        color="secondary"
        disabled={!!loader}
        onClick={props.onClose}
      >{t('Cancel')}</DialogButton>
      <DialogButton
        color="error"
        onClick={onConfirm}
        autoFocus
        disabled={!confirm || !!loader}
      >{t('Delete')}</DialogButton>
    </DialogActions>
  </Dialog>
}
